import {
  HHButton,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import AddIcon from '@mui/icons-material/Add';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { routes } from '../../constants/strings/routes';
import { useDynamicDrawerContext } from '../../context/dynamic-drawer.context';
import { useSnackbarContext } from '../../context/snackbar.context';
import { useBillingWriteAccess } from '../../hooks/use-billing-write-access';
import { useCreateProgramCapMutation } from '../../types';
import { formatDate } from '../../utils/date-helper';
import { ProgramCapForm } from './form/form';
import { ProgramCapFormValues } from './form/validation-schema';
import { ProgramCapFilter } from './program-cap-filter';
import { ProgramCapGrid } from './program-cap-grid';

export const ProgramCaps = (): JSX.Element => {
  // ACCOUNTS-AND-PLAN-MIGRATION: clientId is client-scoped, not resolvable via contract_id — will be addressed by the Accounts & Plan migration.
  const [clientId, setClientId] = useState<number | null>(null);
  const canWrite = useBillingWriteAccess();
  const navigate = useNavigate();
  const { setDrawerContent, onClose } = useDynamicDrawerContext();
  const { setSnackbarState } = useSnackbarContext();
  const [createProgramCap] = useCreateProgramCapMutation();

  const handleCreateProgramCap = async (
    values: ProgramCapFormValues,
  ): Promise<void> => {
    const input = {
      clientId: values.clientId,
      startDate: formatDate(values.startDate, 'YYYY-MM-DD'),
      endDate: formatDate(values.endDate, 'YYYY-MM-DD'),
      cap: values.cap,
    };

    try {
      await createProgramCap({
        variables: { createProgramCapInput: input },
        refetchQueries: ['GetProgramCaps'],
      });
      setSnackbarState('Program cap created successfully', 'success', 3000);
      onClose();
    } catch {
      setSnackbarState('Error creating program cap', 'error', 3000);
    }
  };

  const handleAddProgramCap = (): void => {
    setDrawerContent(
      <HHStack padding={4}>
        <ProgramCapForm onSubmit={handleCreateProgramCap} />
      </HHStack>,
      <HHTypography hhVariant="h6">Add Program Cap</HHTypography>,
      undefined,
      'temporary',
    );
  };

  return (
    <>
      <HHStack
        spacing={2}
        padding={4}
        alignItems="flex-end"
        direction="row"
        justifyContent="space-between"
      >
        <ProgramCapFilter filterCallback={setClientId} />
        {canWrite && (
          <HHStack direction="row" spacing={2}>
            <HHButton
              hhVariant="variant-bypass"
              variant="outlined"
              size="medium"
              startIcon={<UploadFileIcon />}
              onClick={(): void =>
                navigate(
                  `${routes.billing.home}${routes.billing.batchProgramCapImport}`,
                )
              }
            >
              Import CSV
            </HHButton>
            <HHButton
              hhVariant="contained"
              variant="contained"
              size="medium"
              startIcon={<AddIcon />}
              onClick={handleAddProgramCap}
            >
              Add Program Cap
            </HHButton>
          </HHStack>
        )}
      </HHStack>
      <ProgramCapGrid clientId={clientId} />
    </>
  );
};
